import React, { useEffect, useRef } from "react";
import { Box } from "@mui/system";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";

import ButtonsPlayer from "./ButtonsPlayer";
import TimeLine from "./TimeLine";
import NextPrevTitle from "./NextPrevTitle";
import HeaderMediaPlayer from "./HeaderMediaPlayer";
import Player from "../Player";
import {
  getPlayerState,
  setMinimizePlayer,
  setMinimizePlayerBackUrl,
  setTopicData,
} from "../../../redux/features/playerSlice";
import CustomHelmet from "../CustomHelmet";
import { fetcherGetToken, fetcherPost } from "../../../services/fetcher";
import { getSettingState } from "../../../redux/features/settingSlice";

const MainPlayer = () => {
  const ref = useRef(null);
  const dispatch = useDispatch();
  const { playing, topicData, playedPercent, duration } =
    useSelector(getPlayerState);
  const { userData } = useSelector(getSettingState);
  const params = useParams();
  const location = useLocation();
  let urlParams = location.pathname.split("/");
  const topicMode = urlParams[3] === "opinions";
  const urlLink = topicMode
    ? `/table/${params.tableId}/opinions/${params.opinionId}/topics/`
    : `/table/${params.tableId}/discussions/${params.discussionId}/topics/`;
  const tokenStorage = JSON.parse(localStorage.getItem("token")) || null;

  const conditionalReq = () => {
    let mode = null;

    if (topicMode) {
      mode = `/content/topic/${params.topicId}/`;
    } else {
      mode = `/content/converse/${params.topicId}/`;
    }

    return mode;
  };

  const { data: topic, isLoading } = useSWR(
    () => {
      return [process.env.REACT_APP_API + conditionalReq(), tokenStorage];
    },
    fetcherGetToken,

    {
      revalidateOnFocus: false,
      refreshWhenOffline: false,
      refreshWhenHidden: false,
      refreshInterval: 0,
    }
  );

  useEffect(() => {
    return () => {
      if (playing) {
        dispatch(setMinimizePlayer(true));
      } else {
        dispatch(setMinimizePlayer(false));
      }
    };
  }, [dispatch, playing, topic]);

  useEffect(() => {
    if (topic) {
      dispatch(setTopicData(topic));
    }

    return () => {
      if (topic) {
        dispatch(setMinimizePlayerBackUrl(location.pathname));
        dispatch(setTopicData(topic));
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic]);

  const { trigger } = useSWRMutation(
    () => {
      if (!topicData || !userData) {
        return null;
      }
      return [
        `${process.env.REACT_APP_API}/content/log-play/${
          urlParams[3] === "opinions" ? "topic" : "converse"
        }/${params.topicId}/`,
        tokenStorage,
      ];
    },

    fetcherPost
  );

  window.onpopstate = function () {
    trigger({ time_watched: playedPercent * duration });
  };

  return (
    <>
      {topicData && <CustomHelmet title={`${topicData.title} | Player`} />}
      <Box
        className='player-page'
        sx={{
          height: "100vh",
          position: "relative",
          display: "flex",
          justifyContent: "center",
          backgroundColor: "#212121",
        }}
      >
        <Player ref={ref} data={topicData} trigger={trigger} />

        <HeaderMediaPlayer data={topicData} />
        <Box
          sx={{
            position: "fixed",
            bottom: "0px",
            right: "20px",
            left: "20px",
            height: "140px",
          }}
        >
          <ButtonsPlayer
            data={topicData}
            urlLink={urlLink}
            topicMode={topicMode}
            trigger={trigger}
          />
          <Box sx={{ position: "absolute", bottom: "5px", width: "100%" }}>
            <TimeLine ref={ref} data={topicData} />
          </Box>
        </Box>
        <NextPrevTitle
          data={topicData}
          urlLink={urlLink}
          topicMode={topicMode}
          isLoading={isLoading}
          trigger={trigger}
        />
      </Box>
    </>
  );
};

export default MainPlayer;
